import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import * as Mui from "@mui/material";
import { DateRange } from "@mui/icons-material";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { customAPI } from "services/utils/CustomAPI";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  infoStyle,
  childBoxStyle,
} from "services/constants";
import { designColors } from "services/constants/designTokens";
import moment from "moment";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoComplete } from "components/CustomComponents";

const dayoffType = [
  {
    label: "Week Off",
  },
  {
    label: "Holiday",
  },
];

export const CreateDayOff = ({ open = true, onClose, reCallApi }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const [offdayDetails, setOffdayDetails] = useState({
    weekOffDates: new Set(),
    holidayDates: new Set(),
  });
  const [offdayDetailsLoaded, setOffdayDetailsLoaded] = useState(false);
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      dayoff_type: "",
      start_date: moment(),
      end_date: moment(),
      reason_for_dayoff: "",
    },
  });
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const selectedDayoffType = watch("dayoff_type");

  useEffect(() => {
    if (!open || !domain || !userDetails?.id) return;

    const fetchOffdayDetails = async () => {
      setOffdayDetailsLoaded(false);
      const startDate = moment().subtract(1, "year").format("YYYY-MM-DD");
      const endDate = moment().add(1, "year").format("YYYY-MM-DD");
      const response = await customAPI(
        `${domain}my_zone/apply/day_off/myworkspace-dayoff-approval/?user_id=${userDetails?.id}&include_offday_details=true&start_date=${startDate}&end_date=${endDate}&company_id=${requestCompanyId}`,
        "GET",
      );

      if (!response.ok) return;
      const details = response?.responseDetails?.offday_details || {};
      setOffdayDetails({
        weekOffDates: new Set(
          (details.week_off_details || []).map((item) => item.date),
        ),
        holidayDates: new Set(
          (details.holiday_details || []).map((item) => item.date),
        ),
      });
      setOffdayDetailsLoaded(true);
    };

    fetchOffdayDetails();
  }, [domain, open, userDetails?.id, requestCompanyId]);

  const isInvalidOffdayDate = useMemo(
    () => (date) => {
      const selectedType = selectedDayoffType;
      if (!selectedType || !offdayDetailsLoaded) return false;

      const dateKey = moment(date).format("YYYY-MM-DD");
      if (selectedType === "Week Off") {
        return !offdayDetails.weekOffDates.has(dateKey);
      }
      if (selectedType === "Holiday") {
        return !offdayDetails.holidayDates.has(dateKey);
      }
      return false;
    },
    [
      offdayDetails.holidayDates,
      offdayDetails.weekOffDates,
      offdayDetailsLoaded,
      selectedDayoffType,
    ],
  );

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const payload = {
      user_id: userDetails?.id,
      start_date: watch("start_date").format("YYYY-MM-DD"),
      end_date: watch("end_date").format("YYYY-MM-DD"),
      reason: data?.reason_for_dayoff,
      approval_type: data?.dayoff_type,
    };

    // Unified Off-Day Duty endpoint. POST here creates the Pre-Dayoff request
    // (My Workspace only applies Pre-Dayoff), replacing myzone-pre-day-off/.
    const response = await customAPI(
      `${domain}my_zone/apply/day_off/myworkspace-dayoff-approval/?company_id=${requestCompanyId}`,
      "POST",
      payload,
    );
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      reCallApi(true);
      onClose();
      reset();
      setValue("button_loader", false);
    } else {
      // On failure customAPI already surfaces the error toast.
      setValue("button_loader", false);
    }
  };

  const selectDayoffType = (event, data) => {
    if (data) {
      setValue("dayoff_type", data?.label);
    }
  };

  const label =
    watch("start_date").format("DD/MM/YYYY") +
    " - " +
    watch("end_date").format("DD/MM/YYYY");

  const handleReason = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_dayoff", event.target.value);
    }
  };

  const handleCallback = async (startDate, endDate) => {
    setValue("start_date", startDate);
    setValue("end_date", endDate);
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Off-Day Duty Request"
      subtitle="Create an Off-Day Duty request for a selected period"
      bodySx={{ backgroundColor: designColors.pageBg }}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            width: "100%",
          }}
        >
          <SecondaryButton
            onClick={onClose}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form="dayoff-request-form"
            loading={Boolean(watch("button_loader"))}
            disabled={Boolean(watch("button_loader"))}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Apply
          </PrimaryButton>
        </Box>
      }
    >
      <form id="dayoff-request-form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 2 }}>
          <Box sx={childBoxStyle}>
            <RequestDetailHeader
              userDetails={{
                image_url: userDetails?.image_url,
                employee_id: userDetails?.employee_id || "-",
                user_name:
                  userDetails?.first_name ||
                  userDetails?.user_name ||
                  userDetails?.username ||
                  "-",
                department_name:
                  userDetails?.department_name || userDetails?.department || "-",
                designation:
                  userDetails?.designation || userDetails?.designation_name || "-",
              }}
            />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Off-Day Date / Period
              </Typography>
              <Typography
                {...infoStyle}
                sx={{
                  ...infoStyle.sx,
                  mb: 1,
                }}
              >
                Select the start and end dates for the off-day duty period
              </Typography>
              <DateRangePicker
                key={`${selectedDayoffType}-${offdayDetails.weekOffDates.size}-${offdayDetails.holidayDates.size}`}
                initialSettings={{
                  startDate: watch("start_date").toDate(),
                  endDate: watch("end_date").toDate(),
                  applyClass: "custom-apply-btn",
                  cancelClass: "custom-cancel-btn",
                  locale: {
                    format: "DD/MM/YYYY",
                  },
                  isInvalidDate: isInvalidOffdayDate,
                }}
                onCallback={handleCallback}
              >
                <Mui.TextField
                  id="reportrange"
                  variant="outlined"
                  label=""
                  placeholder="Select a date range"
                  value={label}
                  size="small"
                  InputProps={{
                    readOnly: true,
                    notched: false,
                    endAdornment: (
                      <Mui.InputAdornment position="end">
                        <Mui.IconButton>
                          <DateRange />
                        </Mui.IconButton>
                      </Mui.InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true, required: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      legend: {
                        display: "none",
                      },
                    },
                  }}
                />
              </DateRangePicker>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Off-Day Type</Typography>
              <Typography
                {...infoStyle}
                sx={{
                  ...infoStyle.sx,
                  mb: 0,
                }}
              >
                Select the off-day type for further reference
              </Typography>
              <CustomAutoComplete
                dataList={dayoffType}
                rules={{
                  ...register("dayoff_type", {
                    required: "Off-day type is required",
                  }),
                }}
                selectedValue={watch("dayoff_type")}
                updateDataFunction={selectDayoffType}
                placeHolder="Select off-day type"
                errorMessage={
                  errors?.dayoff_type?.message
                    ? errors?.dayoff_type?.message
                    : ""
                }
                textBoxError={errors?.dayoff_type ? true : false}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
              <Typography {...infoStyle}>
                Mention the purpose or justification for the off-day duty request
              </Typography>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason_for_dayoff", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason_for_dayoff")}
                updateDataFunction={handleReason}
                placeHolder="Enter reason or justification for off-day duty"
                errorMessage={
                  errors?.reason_for_dayoff?.message
                    ? errors?.reason_for_dayoff?.message
                    : ""
                }
                textBoxError={errors?.reason_for_dayoff ? true : false}
              />
            </Box>

          </Box>
        </Box>
      </form>
    </GlobalSidePanel>
  );
};
